<script setup lang="tsx">
import { ref, onBeforeMount, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { MethodHistory, MethodHistoryData, MethodSnapshot, Response, RuleHistory, RuleSnapshot } from '@/models/api-generated'
import http from '@/services/http'
import { formatDate, formatDateTime, getMethodStatusText, getRuleStatusText } from '@/services/utils'
import PaginationComponent from '../widgets/PaginationComponent.vue'
import { type Change, type MethodHistoryItem, type ParentRule, type Subrule } from '@/types/method-history'
import type { ValueOf } from '@/types/utils'
import type { PaginationState } from '@/types/page'

const props = defineProps({
	metadataId: Number,
	reference: String,
	assetName: String,
})

const methodHistory = ref<MethodHistoryItem[]>()
const totalPages = ref(1)
const pagination = ref<PaginationState>({ page: 1, limit: 50 })

const { t } = useI18n()

const fetchHistory = async (page = 1) => {
	const params = {
		page,
	}

	const { data } = await http.get<Response<MethodHistoryData>>(`api/history/method/${props.metadataId}`, { params })

	return data.data
}

const transformRuleChanges = (before: RuleSnapshot, after: RuleSnapshot) => {
	const changes: Change[] = []

	for (const [key, value] of Object.entries(before)) {
		const typedKey = key as keyof RuleSnapshot

		let b = value
		let a = after[typedKey]

		if (b !== a) {
			if (typedKey === 'status') {
				b = getRuleStatusText(b)
				a = getRuleStatusText(a)
			} else if (typedKey === 'comment') {
				b = `"${b}"`
				a = `"${a}"`
			}

			const change: Change = {
				field: typedKey,
				before: b,
				after: a,
			}

			changes.push(change)
		}
	}

	return changes
}

const mapSubrulesToParent = (fetchedRules: RuleHistory[]) => {
	const ruleMap = new Map<number, ParentRule>()

	fetchedRules.sort((a, b) => a.value.localeCompare(b.value))

	fetchedRules.forEach((rule) => {
		const parentId = rule.parent?.metadataId ?? rule.metadataId
		const isSubrule = !!rule.parent
		const keyExists = ruleMap.has(parentId)
		const changes = transformRuleChanges(rule.before, rule.after)

		if (!changes.length) return

		if (keyExists && isSubrule) {
			const subrule: Subrule = {
				id: rule.metadataId,
				value: rule.value,
				changes,
			}

			if (subrule.changes.length) {
				const parent = ruleMap.get(parentId)

				parent!.subrules.push(subrule)
			}
		} else if (!keyExists) {
			const parent: ParentRule = {
				id: parentId,
				value: rule.parent?.value ?? rule.value,
				changes: [],
				changeCount: 0,
				subrules: [],
			}

			if (isSubrule) {
				const subrule: Subrule = {
					id: rule.metadataId,
					value: rule.value,
					changes,
				}

				parent.subrules.push(subrule)
			} else {
				parent.changes = changes
			}

			ruleMap.set(parentId, parent)
		}
	})

	return ruleMap
}

const transformRules = (fetchedRules: RuleHistory[]) => {
	const ruleMap = mapSubrulesToParent(fetchedRules)

	return Array.from(ruleMap.values()).map((r: ParentRule) => {
		r.changeCount = r.changes.length + countSubruleChanges(r.subrules)

		return r
	})
}

const countRuleChanges = (parentRules: ParentRule[]): number => {
	return parentRules.reduce((total, pr) => total + pr.changeCount, 0)
}

const countSubruleChanges = (subrules: Subrule[]): number => {
	return subrules.reduce((total, sr) => total + sr.changes.length, 0)
}

const transformFieldChange = (typedKey: string, before: ValueOf<MethodSnapshot>, after: ValueOf<MethodSnapshot>): Change | null => {
	switch (typedKey) {
		case 'comment':
			before = `"${before}"`
			after = `"${after}"`
			break
		case 'status':
			before = getMethodStatusText(before)
			after = getMethodStatusText(after)
			break
		case 'due_date':
			before = formatDate(before) || t('misc.unspecified')
			after = formatDate(after) || t('misc.unspecified')
			break
		case 'priority':
			before = before.length ? t(before) : t('misc.unspecified')
			after = after.length ? t(after) : t('misc.unspecified')
			break
		case 'responsible':
			before = before.id ? before.label : t('misc.unspecified')
			after = after.id ? after.label : t('misc.unspecified')
			break
		case 'updated_at':
			before = formatDate(before) || t('unspecified')
			after = formatDate(after) || t('unspecified')
			break
		case 'jira_url':
			before = `"${before}"`
			after = `"${after}"`
			break
	}

	return before !== after ? { field: typedKey, before, after } : null
}

const extractMethodChanges = (item: MethodHistory): Change[] => {
	if (!item.before && !item.after) return []

	return Object.keys(item.before ?? item.after).reduce<Change[]>((acc, key) => {
		const typedKey = key as keyof MethodSnapshot
		const before = item.before[typedKey]
		const after = item.after[typedKey]
		const change = transformFieldChange(key, before, after)
		if (change) acc.push(change)
		return acc
	}, [])
}

const transformHistory = (fetchedHistory: MethodHistory[]) =>
	fetchedHistory.map((item) => {
		const changes = extractMethodChanges(item)

		let rules: ParentRule[] = []
		let ruleChangesCount = 0

		if (item.rules) {
			rules = transformRules(item.rules)
			ruleChangesCount = countRuleChanges(rules)
		}

		const historyItem: MethodHistoryItem = {
			id: item.id,
			firstname: item.firstname,
			lastname: item.lastname,
			createdAt: item.createdAt,
			changes: changes,
			changeCount: changes.length + ruleChangesCount,
			rules,
		}

		return historyItem
	})

const updateHistoryRef = async (page?: number) => {
	const response = await fetchHistory(page)

	if (response) {
		methodHistory.value = transformHistory(response.methodHistory)
		totalPages.value = response.totalPages
	}
}

onBeforeMount(async () => {
	await updateHistoryRef()
})

watch(
	() => pagination.value.page,
	async (newPage) => {
		await updateHistoryRef(newPage)
	},
)
</script>

<template>
	<h5 class="mb-4">{{ `${assetName} - ${reference}` }}</h5>
	<div class="mt-4 ms-2">
		<div :key="item.id" v-for="(item, i) in methodHistory">
			<p>
				{{
					t('history.changeNotice', {
						firstname: item.firstname,
						lastname: item.lastname,
						count: item.changeCount,
						time: formatDateTime(item.createdAt),
					})
				}}
			</p>
			<p class="small-text" v-for="change in item.changes" :key="change.field">
				<strong>{{ t(`history.method.fields.${change.field}`) }}: </strong>{{ `${change.before} -> ${change.after}` }}
			</p>

			<p v-if="item.rules.length" class="small-text">
				<strong>{{ t('history.rules.label') }}:</strong>
			</p>

			<div v-for="rule in item.rules" :key="rule.id">
				<span v-one-tooltip="rule.value" class="small-text mb-2 text-truncate rule-value">
					{{ rule.value }}
				</span>

				<p class="small-text ms-2" v-for="change in rule.changes" :key="change.field">
					<strong>{{ t(`history.rules.fields.${change.field}`) }}: </strong>{{ `${change.before} -> ${change.after}` }}
				</p>

				<div class="ms-2" v-for="subrule in rule.subrules" :key="subrule.id">
					<span v-one-tooltip="subrule.value" class="small-text mb-2 text-truncate rule-value">
						{{ subrule.value }}
					</span>

					<p class="small-text ms-2" v-for="change in subrule.changes" :key="change.field">
						<strong>{{ t(`history.rules.fields.${change.field}`) }}: </strong>{{ `${change.before} -> ${change.after}` }}
					</p>
				</div>
			</div>
			<hr v-if="methodHistory && i < methodHistory.length - 1" class="my-4" />
		</div>

		<PaginationComponent v-if="totalPages > 1" v-model="pagination" :total-pages="totalPages" :disableLimitSelect="true" />
	</div>

	<br />
	<br />
	<br />
</template>

<style lang="sass" scoped>
@import '~bootstrap/scss/functions'
@import '~bootstrap/scss/variables'

.small-text
    font-size: $small-font-size
.rule-value
    display: inline-block
    max-width: 75%
</style>
